import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { AlertTriangle, CheckCircle2, Clock, CalendarCheck } from "lucide-react";

/**
 * Componente de diálogo para perguntar se o usuário quer registrar
 * um item/envio/rotina como já realizado quando criado após o horário/dia.
 * 
 * Props:
 * - open: boolean - Controla se o diálogo está aberto
 * - onOpenChange: (open: boolean) => void - Callback quando o estado muda
 * - onConfirmRegistrar: () => void - Callback quando escolhe registrar como realizado
 * - onConfirmIgnorar: () => void - Callback quando escolhe ignorar (alertar só no próximo)
 * - tipo: "envio" | "item" | "rotina" - Tipo do registro
 * - nomeItem: string - Nome do item sendo criado
 * - detalhes: string - Detalhes adicionais (ex: "Dia 15 - Mensal", "Horário: 09:00")
 */
export const ConfirmacaoRegistroDialog = ({
  open,
  onOpenChange,
  onConfirmRegistrar,
  onConfirmIgnorar,
  tipo = "envio",
  nomeItem = "",
  detalhes = "",
}) => {
  const [loading, setLoading] = useState(false);

  const tipoLabels = {
    envio: {
      titulo: "Envio já deveria ter sido realizado",
      descricao: "O dia configurado para este envio já passou neste mês.",
      registrar: "Já realizei este envio",
      ignorar: "Começar a alertar no próximo mês",
    },
    item: {
      titulo: "Verificação já deveria ter sido realizada",
      descricao: "O horário configurado para verificação já passou hoje.",
      registrar: "Já realizei a verificação de hoje",
      ignorar: "Começar a alertar amanhã",
    },
    rotina: {
      titulo: "Rotina já deveria ter sido realizada",
      descricao: "Esta rotina deveria ter sido executada hoje.",
      registrar: "Já realizei esta rotina hoje",
      ignorar: "Começar a alertar amanhã",
    },
  };

  const labels = tipoLabels[tipo] || tipoLabels.envio;

  const handleRegistrar = async () => {
    setLoading(true);
    try {
      await onConfirmRegistrar();
    } finally {
      setLoading(false);
      onOpenChange(false);
    }
  };

  const handleIgnorar = async () => {
    setLoading(true);
    try {
      await onConfirmIgnorar();
    } finally {
      setLoading(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="confirmacao-registro-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-100">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <DialogTitle className="text-lg">{labels.titulo}</DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Informação do item */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="font-medium text-slate-900">{nomeItem}</p>
            {detalhes && (
              <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {detalhes}
              </p>
            )}
          </div>

          {/* Descrição */}
          <p className="text-sm text-slate-600">
            {labels.descricao} O que você gostaria de fazer?
          </p>

          {/* Opções */}
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300"
              onClick={handleRegistrar}
              disabled={loading}
              data-testid="btn-registrar-realizado"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-emerald-100">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-emerald-700">{labels.registrar}</p>
                  <p className="text-xs text-slate-500">Marcar como realizado e não alertar</p>
                </div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
              onClick={handleIgnorar}
              disabled={loading}
              data-testid="btn-ignorar-periodo"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100">
                  <CalendarCheck className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-blue-700">{labels.ignorar}</p>
                  <p className="text-xs text-slate-500">Não realizei ainda, começar a alertar depois</p>
                </div>
              </div>
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmacaoRegistroDialog;
